const configElement = document.getElementById('CaleoFreeGiftConfig');

if (configElement) {
  const config = JSON.parse(configElement.textContent);
  const products = config.products.filter((product) => product && product.id);
  const giftProductIds = new Set(products.map((product) => Number(product.id)));
  const giftVariantIds = new Set(
    products.flatMap((product) => product.variants || []).map((variant) => Number(variant.id))
  );
  const giftProperty = '_caleo_free_gift';
  const minimumItems = Math.max(2, Number(config.minimum_items) || 2);
  let isMutating = false;
  let refreshTimer;

  const moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  });

  function isGiftLine(item) {
    return (
      item.properties?.[giftProperty] === 'true' ||
      giftProductIds.has(Number(item.product_id))
    );
  }

  function getGiftLines(cart) {
    return cart.items.filter(isGiftLine);
  }

  function qualifyingItemCount(cart) {
    return cart.items.reduce(
      (count, item) => count + (isGiftLine(item) ? 0 : Math.max(0, Number(item.quantity) || 0)),
      0
    );
  }

  function isEligible(cart) {
    return qualifyingItemCount(cart) >= minimumItems;
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to refresh cart.');
    return response.json();
  }

  async function postCart(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Unable to update cart.');
    }
    return data;
  }

  function announceCartRefresh() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function getMountPoint() {
    const root = document.getElementById('rapi-cart-drawer-app');
    const body = root?.querySelector('[data-rapi-cart-body], .rapi-cart-drawer__body');
    return { root, body };
  }

  function setCheckoutDisabled(disabled) {
    const checkout = document.querySelector('#rapi-cart-drawer-app [data-rapi-checkout]');
    if (!(checkout instanceof HTMLButtonElement)) return;
    checkout.disabled = disabled;
    checkout.setAttribute('aria-disabled', disabled.toString());
  }

  function createElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function variantImage(variant, product) {
    return variant.featured_image?.src || product.featured_image || product.images?.[0] || '';
  }

  function renderSelector(cart) {
    const { root, body } = getMountPoint();
    if (!root || !body || products.length === 0) return;

    const eligible = isEligible(cart);
    root.classList.toggle('caleo-free-gift-eligible', eligible);
    let selector = root.querySelector('[data-caleo-free-gift-rapi]');
    if (!selector) {
      selector = createElement('section', 'adv__caleo-free-gift-rapi');
      selector.dataset.caleoFreeGiftRapi = '';
      selector.setAttribute('aria-labelledby', 'CaleoFreeGiftRapiHeading');
      body.insertAdjacentElement('afterend', selector);
    }
    selector.classList.toggle('is-locked', !eligible);
    selector.setAttribute('aria-disabled', (!eligible).toString());

    const giftLines = getGiftLines(cart);
    const selectedVariantId = giftLines.length === 1 ? Number(giftLines[0].variant_id) : 0;
    const pendingVariantIds = new Map(
      Array.from(selector.querySelectorAll('[data-caleo-gift-variant]')).map((select) => [
        Number(select.dataset.productId),
        Number(select.value),
      ])
    );
    selector.replaceChildren();

    const headingRow = createElement('div', 'adv__caleo-free-gift-rapi__heading-row');
    const headingCopy = createElement('div');
    headingCopy.append(
      createElement('p', 'adv__caleo-free-gift-rapi__eyebrow', config.eyebrow),
      createElement('h3', 'adv__caleo-free-gift-rapi__heading', config.heading)
    );
    headingCopy.lastElementChild.id = 'CaleoFreeGiftRapiHeading';
    headingRow.append(headingCopy, createElement('span', 'adv__caleo-free-gift-rapi__value', '$0'));
    selector.append(headingRow, createElement('p', 'adv__caleo-free-gift-rapi__intro', config.intro));

    const lockedMessage = createElement(
      'p',
      'adv__caleo-free-gift-rapi__locked-message',
      config.locked_message || 'Pick at least 2 products to unlock a free gift worth $75.'
    );
    lockedMessage.id = 'CaleoFreeGiftRapiLockedMessage';
    lockedMessage.setAttribute('role', 'status');
    const lockIcon = createElement('span', 'adv__caleo-free-gift-rapi__lock-icon');
    lockIcon.setAttribute('aria-hidden', 'true');
    lockIcon.innerHTML =
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>';
    lockedMessage.prepend(lockIcon);
    selector.append(lockedMessage);
    if (!eligible) {
      selector.setAttribute('aria-describedby', lockedMessage.id);
    } else {
      selector.removeAttribute('aria-describedby');
    }

    const options = createElement('div', 'adv__caleo-free-gift-rapi__options');
    options.setAttribute('role', 'region');
    options.setAttribute('aria-roledescription', 'carousel');
    options.setAttribute('aria-label', 'Free gift options');
    options.tabIndex = eligible ? 0 : -1;

    const selectableProducts = products.filter((product) =>
      (product.variants || []).some((variant) => variant.available !== false)
    );
    for (const [productIndex, product] of selectableProducts.entries()) {
      const availableVariants = (product.variants || []).filter((variant) => variant.available !== false);

      const productSelectedVariant = availableVariants.find((variant) => Number(variant.id) === selectedVariantId);
      const pendingVariant = availableVariants.find(
        (variant) => Number(variant.id) === pendingVariantIds.get(Number(product.id))
      );
      const defaultVariant = pendingVariant || productSelectedVariant || availableVariants[0];
      const isCurrentSelection = Number(defaultVariant.id) === selectedVariantId;
      const card = createElement(
        'article',
        `adv__caleo-free-gift-rapi__card${productSelectedVariant ? ' is-selected' : ''}`
      );
      card.setAttribute('role', 'group');
      card.setAttribute('aria-roledescription', 'slide');
      card.setAttribute(
        'aria-label',
        `${productIndex + 1} of ${selectableProducts.length}: ${product.title}${productSelectedVariant ? ', selected' : ''}`
      );
      const image = createElement('img', 'adv__caleo-free-gift-rapi__image');
      image.src = variantImage(defaultVariant, product);
      image.alt = product.title;
      image.width = 152;
      image.height = 152;
      image.loading = 'lazy';

      const details = createElement('div', 'adv__caleo-free-gift-rapi__details');
      details.append(createElement('h4', 'adv__caleo-free-gift-rapi__title', product.title));
      const price = createElement('p', 'adv__caleo-free-gift-rapi__price');
      price.append(
        createElement('strong', '', 'FREE'),
        createElement('s', '', moneyFormatter.format(Number(defaultVariant.price || 0) / 100))
      );
      details.append(price);

      const selectId = `CaleoFreeGiftRapiVariant-${product.id}`;
      const label = createElement('label', 'adv__caleo-free-gift-rapi__label', 'Choose variant');
      label.htmlFor = selectId;
      const select = createElement('select', 'adv__caleo-free-gift-rapi__select');
      select.id = selectId;
      select.dataset.caleoGiftVariant = '';
      select.dataset.productId = String(product.id);
      select.disabled = !eligible;

      for (const variant of availableVariants) {
        const option = createElement('option', '', variant.title);
        option.value = String(variant.id);
        option.dataset.image = variantImage(variant, product);
        option.dataset.price = String(variant.price || 0);
        option.selected = Number(variant.id) === Number(defaultVariant.id);
        select.append(option);
      }

      const button = createElement(
        'button',
        'adv__caleo-free-gift-rapi__button',
        isCurrentSelection ? 'Selected' : 'Choose gift'
      );
      button.type = 'button';
      button.dataset.caleoChooseGift = '';
      button.dataset.productId = String(product.id);
      button.disabled = !eligible || isCurrentSelection;

      select.addEventListener('change', () => {
        const selectedOption = select.selectedOptions[0];
        image.src = selectedOption.dataset.image || image.src;
        price.querySelector('s').textContent = moneyFormatter.format(Number(selectedOption.dataset.price || 0) / 100);
        const isCurrent = Number(select.value) === selectedVariantId;
        button.disabled = !eligible || isCurrent;
        button.textContent = isCurrent ? 'Selected' : 'Choose gift';
      });
      button.addEventListener('click', () => replaceGift(select.value, selector, giftLines, button));

      details.append(label, select, button);
      card.append(image, details);
      options.append(card);
    }

    const status = createElement('p', 'adv__caleo-free-gift-rapi__status');
    status.dataset.caleoGiftStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    selector.append(options, status);
    decorateGiftLines(cart);
  }

  function decorateGiftLines(cart) {
    const root = document.getElementById('rapi-cart-drawer-app');
    if (!root) return;

    root.querySelectorAll('.caleo-free-gift-line').forEach((row) => row.classList.remove('caleo-free-gift-line'));
    root.querySelectorAll('.caleo-free-gift-line__badge').forEach((badge) => badge.remove());

    for (const item of getGiftLines(cart)) {
      const keyedControl = root.querySelector(`[data-rapi-line-key="${CSS.escape(item.key)}"]`);
      const row = keyedControl?.closest('.rapi-cart-drawer__item');
      if (!row) continue;
      row.classList.add('caleo-free-gift-line');
      const name = row.querySelector('.rapi-cart-drawer__name');
      if (name) name.insertAdjacentElement('beforebegin', createElement('span', 'caleo-free-gift-line__badge', 'Free gift'));
    }
  }

  function showStatus(selector, message, isError = false) {
    const status = selector?.querySelector('[data-caleo-gift-status]');
    if (!status) return;
    status.textContent = message;
    status.setAttribute('role', isError ? 'alert' : 'status');
  }

  async function replaceGift(variantId, selector, existingGiftLines = [], actionButton) {
    if (isMutating || !giftVariantIds.has(Number(variantId))) return;
    isMutating = true;
    setCheckoutDisabled(true);
    const actionButtonLabel = actionButton?.textContent || '';
    let didUpdate = false;
    selector?.querySelectorAll('button, select').forEach((control) => {
      control.disabled = true;
    });
    if (actionButton) actionButton.textContent = 'Updating…';
    showStatus(selector, 'Updating your free gift…');

    try {
      if (existingGiftLines.length > 0) {
        await postCart('/cart/update.js', {
          updates: Object.fromEntries(existingGiftLines.map((item) => [item.key, 0])),
        });
      }
      await postCart('/cart/add.js', {
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              [giftProperty]: 'true',
            },
          },
        ],
      });
      announceCartRefresh();
      didUpdate = true;
    } catch (error) {
      showStatus(selector, error instanceof Error ? error.message : 'Unable to update your free gift.', true);
    } finally {
      if (!didUpdate && selector?.isConnected) {
        selector.querySelectorAll('button, select').forEach((control) => {
          control.disabled = false;
        });
      }
      if (!didUpdate && actionButton?.isConnected) actionButton.textContent = actionButtonLabel;
      isMutating = false;
      setCheckoutDisabled(false);
    }
  }

  async function removeGiftLines(giftLines) {
    if (isMutating || giftLines.length === 0) return;
    isMutating = true;
    setCheckoutDisabled(true);
    try {
      await postCart('/cart/update.js', {
        updates: Object.fromEntries(giftLines.map((item) => [item.key, 0])),
      });
      announceCartRefresh();
    } catch (error) {
      const selector = document.querySelector('[data-caleo-free-gift-rapi]');
      showStatus(selector, error instanceof Error ? error.message : 'Please remove the free gift before checkout.', true);
    } finally {
      isMutating = false;
      setCheckoutDisabled(false);
    }
  }

  async function refresh() {
    if (isMutating) return;
    const { root, body } = getMountPoint();
    if (!root || !body) return;

    try {
      const cart = await fetchCart();
      const giftLines = getGiftLines(cart);
      const eligible = isEligible(cart);
      root.classList.toggle('caleo-free-gift-eligible', eligible);

      if (!eligible) {
        renderSelector(cart);
        if (giftLines.length > 0) await removeGiftLines(giftLines);
        return;
      }

      const validGiftLines = giftLines.filter((item) => giftVariantIds.has(Number(item.variant_id)));
      const needsCleanup =
        giftLines.length > 1 ||
        giftLines.some((item) => Number(item.quantity) !== 1) ||
        validGiftLines.length !== giftLines.length;

      if (needsCleanup) {
        const variantToKeep = validGiftLines[0]?.variant_id;
        await removeGiftLines(giftLines);
        if (variantToKeep) setTimeout(() => replaceGift(String(variantToKeep)), 400);
        return;
      }

      renderSelector(cart);
    } catch (error) {
      const selector = root.querySelector('[data-caleo-free-gift-rapi]');
      showStatus(selector, error instanceof Error ? error.message : 'Unable to refresh your free gift.', true);
    }
  }

  function scheduleRefresh() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, 120);
  }

  document.addEventListener('theme:cart:updated', scheduleRefresh);
  document.addEventListener('cart:updated', scheduleRefresh);

  const appObserver = new MutationObserver((mutations) => {
    const relevantMutation = mutations.some((mutation) => {
      const target = mutation.target;
      if (target instanceof Element && target.matches('[data-rapi-cart-body]')) return true;

      return Array.from(mutation.addedNodes).some(
        (node) =>
          node instanceof Element &&
          (node.matches('#rapi-cart-drawer-app, [data-rapi-cart-body]') ||
            node.querySelector('#rapi-cart-drawer-app, [data-rapi-cart-body]'))
      );
    });
    if (relevantMutation) scheduleRefresh();
  });
  appObserver.observe(document.documentElement, { childList: true, subtree: true });
  scheduleRefresh();
}
